"use client";

import dynamic from "next/dynamic";
import { m } from "framer-motion";
import type { ServiceCardVM } from "@/components/ServiceCategoryCarousel";
import ButtonLandingPage from "@/components/landing/ButtonLandingPage";
import { LandingMotion } from "@/components/landing/motion/LandingMotion";
import { landingViewport, slideUp } from "@/lib/landing-motion";

const ServiceCategoryCarousel = dynamic(
    () => import("@/components/ServiceCategoryCarousel"),
    {
        ssr: true,
        loading: () => (
            <div
                className="min-h-[28rem] w-full animate-pulse rounded-[40px] bg-primary/10"
                aria-hidden
            />
        ),
    },
);

type Props = {
    heading: string;
    description?: string | null;
    buttonLabel?: string | null;
    cards: ServiceCardVM[];
};

export default function SeasonalOffersSectionClient({
    heading,
    description,
    buttonLabel,
    cards,
}: Props) {
    const ctaLabel = buttonLabel?.trim() || "Zapytaj o termin";
    const descriptionText = description?.trim() || "";

    return (
        <LandingMotion>
            <m.section
                initial="hidden"
                whileInView="visible"
                viewport={landingViewport}
                className="px-1 py-4 md:px-[5%] md:py-6 lg:py-8"
                aria-label="Oferty sezonowe"
            >
                <div className="mx-auto w-full md:container">
                    <div className="overflow-x-clip rounded-3xl bg-secondary text-primary md:overflow-hidden">
                        <div className="grid grid-cols-1 items-start lg:grid-cols-[minmax(0,1fr)_minmax(0,540px)] lg:items-center xl:grid-cols-[minmax(0,1fr)_minmax(0,560px)]">
                            <div className="flex flex-col justify-center px-5 pt-10 pb-6 pr-6 text-left md:px-6 md:py-12 md:pr-8 lg:px-8 lg:py-16 lg:pr-10 xl:pl-10">
                                <m.p
                                    variants={slideUp}
                                    custom={0}
                                    className="mb-3 text-sm uppercase tracking-[0.10em] text-primary/80 md:mb-4"
                                >
                                    Oferta sezonowa
                                </m.p>
                                <m.h2
                                    variants={slideUp}
                                    custom={1}
                                    className="mb-3 max-w-xl text-3xl font-medium leading-tight md:mb-6 md:text-5xl lg:text-6xl"
                                >
                                    {heading}
                                </m.h2>
                                {descriptionText ? (
                                    <m.p
                                        variants={slideUp}
                                        custom={2}
                                        className="max-w-xl text-base text-primary/90 md:text-md"
                                    >
                                        {descriptionText}
                                    </m.p>
                                ) : null}
                                <m.div variants={slideUp} custom={3} className="mt-6 md:mt-8">
                                    <ButtonLandingPage label={ctaLabel} />
                                </m.div>
                            </div>

                            {cards.length > 0 ? (
                                <m.div
                                    variants={slideUp}
                                    custom={2}
                                    className="min-w-0 w-full pb-12 pl-3 pr-0 md:pb-10 md:pl-10 md:pr-10 lg:overflow-hidden lg:py-12 lg:pl-2 lg:pr-0"
                                >
                                    <ServiceCategoryCarousel
                                        cards={cards}
                                        categoryDescription=""
                                        variant="contained"
                                        theme="dark"
                                        cardSize="large"
                                        loop
                                        fillMobile
                                        equalHeight
                                        priorityCount={0}
                                        showHoverDescription={false}
                                        idPrefix="seasonal-offer"
                                        contentClassName="!ml-0"
                                        controlsClassName="justify-end pr-4 md:pr-2 lg:pr-8"
                                    />
                                </m.div>
                            ) : (
                                <div className="hidden lg:block" />
                            )}
                        </div>
                    </div>
                </div>
            </m.section>
        </LandingMotion>
    );
}
